import { filter } from "lodash";
import { cloneElement, Component, ReactElement } from "react";
import { connect, ConnectedProps } from "react-redux";
import { Subject, Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { Option } from "space-monad";
import styled from "styled-components";
import { TextField } from "../../../components/forms/text";
import { Flex } from "../../../components/layout";
import { Spinner } from "../../../components/loader/icon";
import { Pageable, Sort } from "../../../services/pageable";
import { AppStore } from "../../../store";
import { AuthDTO } from "../../../store/auth/models/dto";
import {
    Fetchable,
    isFetchableSuccess
} from "../../../store/common/fetchable/helpers";
import { UserActions } from "../../../store/users/actions";
import { UsersDTO } from "../../../store/users/models/dto";
import { AnErrorHasOccured } from "./base";
import { PresentationalUsersProps } from "./views/common";
import { PresentationalUsersList } from "./views/users";

const Wrapper = styled(Flex).attrs({ centered: true })`
  justify-content: center;
  height: 20em;
`;

interface UsersComponentInnerProps {
  children: ReactElement<
    PresentationalUsersProps,
    typeof PresentationalUsersList
  >;
}

interface UsersComponentPropsFromState {
  allUsers: {
    data: Fetchable<UsersDTO.Responses.GetAll.T>;
    pagination: Pageable.T<keyof UsersDTO.Responses.GetSingle.T>;
  };
  auth: Fetchable<AuthDTO.Responses.Login.T>;
}

type SetPaginationFunc<K> = (
  search: string,
  sorting?: Sort<keyof K>
) => UserActions.Actions;

type UsersComponentPropsFromDispatch<K> = {
  setPagination: (fields: Array<keyof K>) => SetPaginationFunc<K>;
};

type UsersComponentPropsFromMerge<K> = UsersComponentInnerProps &
  UsersComponentPropsFromState & {
    setPagination: SetPaginationFunc<K>;
  };

const connection = connect<
  UsersComponentPropsFromState,
  UsersComponentPropsFromDispatch<UsersDTO.Responses.GetSingle.T>,
  UsersComponentInnerProps,
  UsersComponentPropsFromMerge<UsersDTO.Responses.GetSingle.T>,
  AppStore
>(
  (store: AppStore) => ({
    allUsers: store.users.allUsers,
    auth: store.auth.login,
  }),
  (dispatch) => ({
    setPagination: (fields: Array<keyof UsersDTO.Responses.GetSingle.T>) => (
      value: string,
      sorting
    ) =>
      dispatch(
        UserActions.setPagination({
          filters: filter(
            fields.map((field) => ({ field, value })),
            ({ value }) => value !== ""
          ),
          sorting,
        })
      ),
  }),
  (stateProps, dispatchProps, ownProps) => ({
    ...ownProps,
    ...stateProps,
    setPagination: dispatchProps.setPagination(
      isFetchableSuccess(stateProps.auth) &&
        stateProps.auth.data.accessToken.roles.includes("ROLE_ADMIN")
        ? ["emailAddress", "firstName", "lastName"]
        : ["emailAddress"]
    ),
  }),
  {}
);

export type UsersComponentProps = ConnectedProps<typeof connection>;

type UsersComponentState = {
  loading: boolean;
  search: string;
  debouncedSearch: string;
};

class UsersComponentInner extends Component<
  UsersComponentProps,
  UsersComponentState
> {
  readonly subscriptions: Subscription[] = [];
  readonly searchSubject;

  constructor(props: UsersComponentProps) {
    super(props);
    this.searchSubject = new Subject<string>();

    this.subscriptions.push(
      this.searchSubject.pipe(debounceTime(500)).subscribe((value) => {
        this.props.setPagination(value, this.props.allUsers.pagination.sorting);
        this.setState({ debouncedSearch: value });
      })
    );
  }

  componentDidMount() {
    if (!isFetchableSuccess(this.props.allUsers.data)) {
      this.props.setPagination("", { field: "createdDate", direction: "desc" });
    }
  }

  componentWillUnmount() {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }

  state = {
    loading: false,
    ...Option(
      (isFetchableSuccess(this.props.allUsers.data) &&
        this.props.allUsers.pagination.filters?.find(
          (x) => x.field === "emailAddress"
        )?.value) ||
        ""
    ).fold(
      () => ({
        search: "",
        debouncedSearch: "",
      }),
      (val) => ({
        search: val,
        debouncedSearch: val,
      })
    ),
  };

  render() {
    const { allUsers, auth } = this.props;

    let componentToRender = null;

    if (allUsers.data.status === "error" || auth.status === "error") {
      componentToRender = <AnErrorHasOccured />;
    } else if (allUsers.data.status === "empty" || auth.status === "empty") {
      componentToRender = (
        <Wrapper>
          <Spinner />
        </Wrapper>
      );
    } else if (allUsers.data.status === "ready" && auth.status === "ready") {
      componentToRender = cloneElement(this.props.children, {
        usersList: allUsers.data.data,
        auth: auth.data,
      });
    }

    if (this.state.debouncedSearch !== this.state.search) {
      componentToRender = (
        <Wrapper>
          <Spinner />
        </Wrapper>
      );
    }

    return (
      <>
        <TextField
          value={this.state.search}
          onChange={(e) => {
            this.setState({ search: e.target.value });
            this.searchSubject.next(e.target.value);
          }}
          fullWidth
          type="text"
          label="Search"
        />
        {componentToRender}
      </>
    );
  }
}

export const UsersSearchComponent = connection(UsersComponentInner);
